import { Component, OnInit, ViewChild, ElementRef, Input, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as CryptoJS from 'crypto-js';
import { ImageUploadService } from 'src/app/services/upload-service.service';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { DateFormatterService } from 'src/app/services/date-formatter.service';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';

interface ImageInfo {
  image: any;
  isLandscape: boolean;
  name: string
}

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss']
})

export class NotificationsComponent implements OnInit {

  @ViewChild('backdrop') backdrop!: ElementRef;
  @ViewChild('mediaLabel') mediaLabel!: ElementRef;
  @ViewChild('mediaUploadButton') mediaUploadButton!: ElementRef;
  @ViewChild('notificationToast') notificationToast!: ElementRef;
  @ViewChild('notificationExpand') notificationExpand!: ElementRef;
  @ViewChild('errorToast') errorToast!: ElementRef;

  toggleBackdrop(show: boolean): void {
    if (show) {
      this.backdrop.nativeElement.style.display = 'block';
    } else {
      this.backdrop.nativeElement.style.display = 'none';
    }
  }

  showErrorToast(message: string): void {
    const toastBody = this.errorToast.nativeElement.querySelector('.toast-body');
    if (toastBody) {
      toastBody.textContent = message;
    }
    this.errorToast.nativeElement.classList.add('show');
    setTimeout(() => {
      this.errorToast.nativeElement.classList.remove('show');
    }, 3000); // Hide after 3 seconds
  }

  @Input() businessData: any;
  notificationForm: FormGroup;
  selectedMedia: { name: string, size: number, invalid: boolean, errorMessage?: string, thumbnail?: any }[] = [];
  validFiles: File[] = [];
  mediaValid: boolean = true;
  mediaUploaded: boolean = false;
  pdfUploaded: boolean = false;
  compressedImages!: [];
  imageInfoArray: (ImageInfo | File)[] = [];  // Store both compressed images and non-image files
  mediaReady: any = [];
  resetDate!: string; 

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private imageUploadService: ImageUploadService,
    public loadingSpinnerService: LoadingSpinnerService,
    private afAuth: AngularFireAuth,
    private firestore: AngularFirestore,
    private formatDateService: DateFormatterService,
    private renderer: Renderer2
  ) {
    this.notificationForm = this.fb.group({
      notificationTitle: ['', [Validators.required, Validators.maxLength(30)]],
      notificationDetails: ['', [Validators.required, Validators.maxLength(500)]],
      findOutMoreLink: ['', [Validators.pattern('https?://.+')]],
      media: [null],
    });
  }

  // Helper methods to access form controls
  get notificationTitle(): AbstractControl {
    return this.notificationForm.controls['notificationTitle'];
  }

  get notificationDetails(): AbstractControl {
    return this.notificationForm.controls['notificationDetails'];
  }

  get findOutMoreLink(): AbstractControl {
    return this.notificationForm.controls['findOutMoreLink'];
  }

  onMediaChange(event: any, type: string) {
    const files = event.target.files;

    if ((files.length + this.selectedMedia.length) > 5) {
      alert("Maximum number of files is 5!");
      return;
    }

    this.mediaValid = true;

    for (let file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type === 'video/mp4';
      const isPdf = file.type === 'application/pdf';
      let invalid = false;
      let errorMessage = '';
      let thumbnail: SafeUrl | string = '';

      // Handle PDF upload
      if (isPdf && type === 'pdf') {
        if (!['application/pdf'].includes(file.type)) {
          invalid = true;
          errorMessage = 'Invalid file type.';
        }
        this.selectedMedia.push({ name: file.name, size: file.size, invalid, errorMessage });
        this.validFiles.push(file);

        // Set the PDF uploaded flag
        this.pdfUploaded = true;
        this.mediaUploaded = false;
        return;
      }

      // Handle media upload
      if (isImage || isVideo) {
        if (isImage && !['image/png', 'image/jpeg'].includes(file.type)) {
          invalid = true;
          errorMessage = 'Invalid image type.';
        } else if (isVideo && file.size > 10 * 1024 * 1024) {
          invalid = true;
          errorMessage = 'Video size exceeds 10 MB.';
        }

        // For valid media, process the thumbnail (image or video)
        if (!invalid) {
          thumbnail = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
          this.validFiles.push(file);
          this.mediaUploaded = true;
          this.pdfUploaded = false;  // Disable PDF if media is uploaded
        }

        this.selectedMedia.push({ name: file.name, size: file.size, invalid, errorMessage, thumbnail });
      } else {
        invalid = true;
        errorMessage = 'Invalid file type.';
      }
      if (invalid) {
        this.mediaValid = false; // If any file is invalid, mark media as invalid
      }
    }
    event.target.value = null;  // Reset the file input
  }

  removeFile(index: number) {
    const removedFile = this.selectedMedia.splice(index, 1)[0];

    if (!removedFile.invalid) {
      const fileIndex = this.validFiles.findIndex(file => file.name === removedFile.name);
      if (fileIndex !== -1) {
        this.validFiles.splice(fileIndex, 1);
      }
    }

    this.mediaValid = this.selectedMedia.every(file => !file.invalid); // Check media validity after removal

    if (this.selectedMedia.some(file => file.name.endsWith('.pdf'))) {
      this.pdfUploaded = true;
      this.mediaUploaded = false;
    } else if (this.selectedMedia.some(file => file.thumbnail)) {
      this.mediaUploaded = true;
      this.pdfUploaded = false;
    } else {
      this.mediaUploaded = false;
      this.pdfUploaded = false;
    }
  }

  async sendNotification() {
    this.loadingSpinnerService.show();

    const businessSnapshot: any = await firstValueFrom(this.firestore.collection('Businesses', ref => ref.where('id', '==', this.businessData.id)).get());
    
    if (businessSnapshot.size > 0) {
        const businessDataNot = businessSnapshot.docs[0].data();
        if (businessDataNot.remainingNotificationsCount <= 0) {
            this.businessData.remainingNotificationsCount = 0;
            this.resetNotificationForm();
            return;
        }
    }

    if (this.validFiles.length > 0) {
      try {
        await this.handleFileUpload();
      } catch (error) {
        console.error('Error uploading files:', error);
        this.showErrorToast('Failed to upload media. Please try again.');
        this.loadingSpinnerService.hide();
        return;
      }
    } else {
      this.mediaReady = [];
    }

    const encryptedAccessToken = localStorage.getItem('EAT');
    let decryptedAccessToken;
    if (encryptedAccessToken) {
      const decryptedBytes = CryptoJS.AES.decrypt(encryptedAccessToken, '9diJh8ETujshkf8WKRuAopfqdDH9Jm7N');
      decryptedAccessToken = decryptedBytes.toString(CryptoJS.enc.Utf8);
    } else {
      this.getNewAccessToken();
    }

    const url = environment.fcmUrl;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${decryptedAccessToken}`,
      'Content-Type': 'application/json'
    });

    let notificationContentType: string = '';

    if (this.mediaReady.length === 0) {
      notificationContentType = "text"
    } else if (this.mediaReady[0].mediaType === "pdf") {
      notificationContentType = "pdf";
    } else if (this.mediaReady[0].mediaType === "video" || this.mediaReady[0].mediaType === "image") {
      notificationContentType = "media";
    }

    // iOS notification payload
    const bodyIos = {
      message: {
        topic: `${this.businessData.id}_ios`,
        notification: {
          title: this.notificationTitle.value,
          body: this.notificationDetails.value,
        },
        data: {
          title: this.notificationTitle.value,
          body: this.notificationDetails.value,
          notificationType: "business",
          buttonActionURL: this.findOutMoreLink.value,
          notificationContentType: notificationContentType,
          media: JSON.stringify(this.mediaReady),
          pdfURL: notificationContentType === 'pdf' ? this.mediaReady[0].link : "",
          businessName: this.businessData.name,
          businessId: this.businessData.id,
          businessLogoUrl: this.businessData.businessImageUrl
        }
      }
    };

    // Android notification payload
    const bodyAndroid = {
      message: {
        topic: `${this.businessData.id}_android`,
        data: {
          title: this.notificationTitle.value,
          body: this.notificationDetails.value,
          notificationType: "business",
          buttonActionURL: this.findOutMoreLink.value,
          notificationContentType: notificationContentType,
          media: JSON.stringify(this.mediaReady),
          businessName: this.businessData.name,
          pdfURL: notificationContentType === 'pdf' ? this.mediaReady[0].link : "",
          businessId: this.businessData.id,
          businessLogoUrl: this.businessData.businessImageUrl
        }
      }
    };

    // Create a promise array to handle both requests
    const notificationPromises = [
      this.http.post(url, bodyIos, { headers }).toPromise(),
      this.http.post(url, bodyAndroid, { headers }).toPromise()
    ];

    try {
      await Promise.all(notificationPromises); // Wait for both requests to finish
      this.firestore.collection('Businesses').doc(this.businessData.id).update({ remainingNotificationsCount: this.businessData.remainingNotificationsCount - 1 });
      this.businessData.remainingNotificationsCount = this.businessData.remainingNotificationsCount - 1
      localStorage.setItem('businessData', JSON.stringify(this.businessData))
      this.loadingSpinnerService.hide();
      const collapseElement = this.notificationExpand.nativeElement;
      setTimeout(() => {
        this.renderer.addClass(collapseElement, 'collapsing');
        this.renderer.removeClass(collapseElement, 'collapse');
        this.renderer.removeClass(collapseElement, 'show');
        this.toggleBackdrop(false);
      }, 350);
      this.notificationToast.nativeElement.classList.add('show');

      // Automatically hide the toast after a few seconds (optional)
      setTimeout(() => {
        this.notificationToast.nativeElement.classList.remove('show');
      }, 2000); // Hide after 3 seconds
      this.resetNotificationForm();
    } catch (error: any) {
      console.error('Error sending notifications:', error);
      if (error.status === 401) {
        this.getNewAccessToken();
        return;
      }
      this.loadingSpinnerService.hide();
    }
  }

  // Reset form and UI elements
private resetNotificationForm() {
  this.notificationForm.reset();
  this.selectedMedia = [];
  this.validFiles = [];
  this.mediaReady = [];
  this.mediaUploaded = false;
  this.pdfUploaded = false;
  this.mediaLabel.nativeElement.classList.remove('active');
  this.loadingSpinnerService.hide();
}

  async getNewAccessToken() {
    try {
      localStorage.removeItem('EAT');

      // Subscribe to the idToken observable to get the JWT
      this.afAuth.idToken.subscribe(async (userAccessToken) => {
        if (userAccessToken) {
          const headers = new HttpHeaders({
            Authorization: `Bearer ${userAccessToken}`,
            'Content-Type': 'application/json'
          });

          const response: any = await this.http.post(environment.accessTokenUrl, {}, { headers }).toPromise();

          const encryptedAccessToken = CryptoJS.AES.encrypt(response.accessToken, '9diJh8ETujshkf8WKRuAopfqdDH9Jm7N').toString();
          localStorage.setItem('EAT', encryptedAccessToken);

          this.sendNotification();
        } else {
          console.error('User not authenticated');
        }
      });
    } catch (error) {
      console.error('Error fetching new access token:', error);
    }
  }


  async handleFileUpload() {
    const countryISO = this.businessData?.location?.countryISO;
    this.mediaReady = await this.imageUploadService.compressAndUploadFiles(
      'Notifications',
      this.businessData.id,
      this.validFiles,
      countryISO 
    );
  }

  checkNotificationReset() {
    const currentTime = this.formatDateService.formatDate(Timestamp.now());
    const lastReset = this.formatDateService.formatDate(this.businessData.lastResetDate);
    const [currentDay, currentMonth, currentYear] = currentTime.split('.');
    const [lastResetDay, lastResetMonth, lastResetYear] = lastReset.split('.');
    
    // Calculate next month and handle year rollover
    let nextMonth = Number(currentMonth) + 1;
    let nextYear = Number(currentYear);
    
    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }
    
    // Format month with leading zero if needed
    const formattedMonth = nextMonth < 10 ? '0' + nextMonth : nextMonth.toString();
    this.resetDate = '01.' + formattedMonth + '.' + nextYear;
    
    if (currentMonth !== lastResetMonth || currentYear !== lastResetYear) {
      this.businessData.remainingNotificationsCount = this.businessData.maximumAllowedNotifications;
      this.businessData.lastResetDate = Timestamp.now();

      this.firestore.collection('Businesses').doc(this.businessData.id).update({
        remainingNotificationsCount: this.businessData.maximumAllowedNotifications,
        lastResetDate: this.businessData.lastResetDate
      });

    }
  }

  ngOnInit(): void {
    this.checkNotificationReset()
  }

}
